import { Icon } from '@natera/material/lib/icon'
import { useStore } from '@tanstack/react-form'
import { useNavigate, useParams } from 'react-router'
import { isNonFinancialEob, useSubcategories } from '~/entities/categories'
import {
  getDocumentStepUrl,
  getDocumentsUrlWithCurrentParams,
  getIsDuplicationFinished,
  useCurrentDocument,
  usePaymentSummary,
  useUpdatePaymentSummary,
} from '~/entities/document'
import { FLAG_TYPES, useStepFlagState } from '~/entities/document-flags'
import { useAuditTrailQueryParams } from '~/features/audit-trail'
import type {
  CheckedPatient,
  PaymentSummaryResponse,
} from '~/shared/api/data-ingestion-api/client/types.gen'
import { useAppForm } from '~/shared/lib/form'
import { useIsScrolled } from '~/shared/lib/use-is-scrolled'
import { RadioButton, Text, Textfield, Tooltip } from '~/shared/ui'
import { ReviewActions } from '~/widgets/review-actions'

type DuplicateStatus = 'not-posted' | 'duplicate' | null

/** Maps the server `no_duplicates_in_amd` boolean to the local radio state. */
function getInitialStatus(noDuplicates: boolean | null | undefined): DuplicateStatus {
  if (noDuplicates === true) return 'not-posted'
  if (noDuplicates === false) return 'duplicate'
  return null
}

interface CheckDuplicateFormValues {
  patient1: {
    patientAccountNumber: string
    patientFirstName: string
    patientLastName: string
  }
  patient2: {
    patientAccountNumber: string
    patientFirstName: string
    patientLastName: string
  }
  status: DuplicateStatus
}

function buildPatientDefaults(
  summaryData: PaymentSummaryResponse | undefined,
): CheckDuplicateFormValues {
  const p1 = summaryData?.checked_patients?.[0]
  const p2 = summaryData?.checked_patients?.[1]
  return {
    patient1: {
      patientAccountNumber: p1?.patient_account_number ?? '',
      patientFirstName: p1?.patient_first_name ?? '',
      patientLastName: p1?.patient_last_name ?? '',
    },
    patient2: {
      patientAccountNumber: p2?.patient_account_number ?? '',
      patientFirstName: p2?.patient_first_name ?? '',
      patientLastName: p2?.patient_last_name ?? '',
    },
    status: getInitialStatus(summaryData?.no_duplicates_in_amd),
  }
}

function buildCheckedPatients(
  patient1: CheckDuplicateFormValues['patient1'],
  patient2: CheckDuplicateFormValues['patient2'],
): CheckedPatient[] {
  const result: CheckedPatient[] = [
    {
      patient_account_number: patient1.patientAccountNumber || null,
      patient_first_name: patient1.patientFirstName || null,
      patient_last_name: patient1.patientLastName || null,
    },
  ]
  const hasPatient2 =
    patient2.patientAccountNumber.trim() ||
    patient2.patientFirstName.trim() ||
    patient2.patientLastName.trim()
  if (hasPatient2) {
    result.push({
      patient_account_number: patient2.patientAccountNumber || null,
      patient_first_name: patient2.patientFirstName || null,
      patient_last_name: patient2.patientLastName || null,
    })
  }
  return result
}

/**
 * Inner content for the duplication check step. Separated from the page shell
 * so that DocumentReviewLayout wraps it at the page level.
 */
export function DuplicationContent() {
  const navigate = useNavigate()
  const documentKey = useParams<'documentKey'>().documentKey as string
  const { data: summaryData } = usePaymentSummary()
  const { mutateAsync: updatePaymentSummary, isPending } = useUpdatePaymentSummary()
  const {
    data: { document },
  } = useCurrentDocument()
  const queryParams = useAuditTrailQueryParams()
  const subcategories = useSubcategories()
  const isNonFinancial = isNonFinancialEob(document.category)
  const { isScrolled: isScrolledBottom, sentinelElement: sentinelElementBottom } = useIsScrolled()
  const stepFlag = useStepFlagState(FLAG_TYPES.STEP_EOB_DUPLICATION)

  const form = useAppForm({
    defaultValues: buildPatientDefaults(summaryData),
    validators: {
      onChange: ({ value }) => {
        const errors: Record<string, string> = {}
        const p1 = value.patient1
        if (!p1.patientAccountNumber.trim()) errors['patient1.patientAccountNumber'] = 'Required'
        if (!p1.patientFirstName.trim()) errors['patient1.patientFirstName'] = 'Required'
        if (!p1.patientLastName.trim()) errors['patient1.patientLastName'] = 'Required'
        if (value.status !== 'not-posted') errors.status = 'Required'
        return Object.keys(errors).length ? { fields: errors } : undefined
      },
      onMount: ({ value }) => {
        const errors: Record<string, string> = {}
        const p1 = value.patient1
        if (!p1.patientAccountNumber.trim()) errors['patient1.patientAccountNumber'] = 'Required'
        if (!p1.patientFirstName.trim()) errors['patient1.patientFirstName'] = 'Required'
        if (!p1.patientLastName.trim()) errors['patient1.patientLastName'] = 'Required'
        if (value.status !== 'not-posted') errors.status = 'Required'
        return Object.keys(errors).length ? { fields: errors } : undefined
      },
    },
  })

  const { status, isValid, isDefaultValue } = useStore(form.store, (state) => ({
    status: state.values.status,
    isValid: state.isValid,
    isDefaultValue: state.isDefaultValue,
  }))

  /** Save duplicate check result and navigate to the next EOB step. */
  async function handleContinue() {
    try {
      await updatePaymentSummary({
        path: { document_key: documentKey },
        body: {
          no_duplicates_in_amd: true,
          checked_patients: buildCheckedPatients(
            form.state.values.patient1,
            form.state.values.patient2,
          ),
          status: null,
        },
      })
    } catch {
      // On failure the user stays on this step.
      return
    }
    navigate(getDocumentStepUrl(documentKey, 'eob_processing', { queryParams }))
  }

  /** Mark as duplicate and return to work queue. */
  async function handleDuplicate() {
    try {
      await updatePaymentSummary({
        path: { document_key: documentKey },
        body: {
          no_duplicates_in_amd: false,
          checked_patients: buildCheckedPatients(
            form.state.values.patient1,
            form.state.values.patient2,
          ),
        },
      })
    } catch {
      // Best-effort save — don't block navigation on failure.
    }
    navigate(getDocumentsUrlWithCurrentParams())
  }

  return (
    <div>
      <div className="px-6 py-4">
        <div className="flex flex-col gap-2">
          <Text appearance="SUBTITLE2">Check for duplicate in AMD</Text>
          <Text
            appearance="CAPTION"
            className="text-text-secondary"
          >
            Search for 1-2 patients from this EOB in AMD to confirm whether it has already been
            posted.
          </Text>
        </div>

        <div className="mt-6 flex flex-col gap-6">
          <div>
            <Text appearance="CAPTION">Patient 1</Text>
            <div className="mt-2 grid grid-cols-3 gap-4">
              <form.Field name="patient1.patientAccountNumber">
                {(field) => (
                  <div className="relative">
                    <Tooltip content="Enter all numbers and letters as shown.">
                      <Icon
                        children="info"
                        className="text-content-high-emphasis absolute top-[17px] left-4 z-1 cursor-pointer"
                      />
                    </Tooltip>
                    <Textfield
                      label="Patient Account Number"
                      leadingIcon={<span />}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      required
                    />
                  </div>
                )}
              </form.Field>
              <form.Field name="patient1.patientFirstName">
                {(field) => (
                  <Textfield
                    label="Patient First Name"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    required
                  />
                )}
              </form.Field>
              <form.Field name="patient1.patientLastName">
                {(field) => (
                  <Textfield
                    label="Patient Last Name"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    required
                  />
                )}
              </form.Field>
            </div>
          </div>

          <div>
            <Text appearance="CAPTION">Patient 2</Text>
            <div className="mt-2 grid grid-cols-3 gap-4">
              <form.Field name="patient2.patientAccountNumber">
                {(field) => (
                  <div className="relative">
                    <Tooltip content="Enter all numbers and letters as shown.">
                      <Icon
                        children="info"
                        className="text-content-high-emphasis absolute top-[17px] left-4 z-1 cursor-pointer"
                      />
                    </Tooltip>
                    <Textfield
                      label="Patient Account Number"
                      leadingIcon={<span />}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                  </div>
                )}
              </form.Field>
              <form.Field name="patient2.patientFirstName">
                {(field) => (
                  <Textfield
                    label="Patient First Name"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                )}
              </form.Field>
              <form.Field name="patient2.patientLastName">
                {(field) => (
                  <Textfield
                    label="Patient Last Name"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                )}
              </form.Field>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <Text appearance="SUBTITLE2">Confirm Posting Status</Text>
          <div className="mt-3 flex gap-4">
            <button
              className="bg-surface-lowest border-input-outlined-enabled-border flex cursor-pointer items-center rounded-sm border px-4"
              type="button"
              onClick={() => form.setFieldValue('status', 'not-posted')}
            >
              <RadioButton
                label="Not posted yet"
                checked={status === 'not-posted'}
                onChange={() => form.setFieldValue('status', 'not-posted')}
              />
            </button>
            <button
              className="bg-surface-lowest border-input-outlined-enabled-border flex cursor-pointer items-center rounded-sm border px-4"
              type="button"
              onClick={() => form.setFieldValue('status', 'duplicate')}
            >
              <RadioButton
                label="Duplicate (already in AMD)"
                checked={status === 'duplicate'}
                onChange={() => form.setFieldValue('status', 'duplicate')}
              />
            </button>
          </div>
        </div>
      </div>

      <ReviewActions
        isDirty={false}
        isSubmitDisabled={
          stepFlag.hasActiveStepFlag
            ? false
            : status === 'duplicate'
              ? isPending
              : !isValid ||
                (getIsDuplicationFinished(document, subcategories) && isDefaultValue) ||
                isPending
        }
        submitTooltip={
          stepFlag.hasActiveStepFlag
            ? undefined
            : status !== 'duplicate' && !isValid
              ? 'Fill in all required fields to validate.'
              : undefined
        }
        onSubmit={
          stepFlag.hasActiveStepFlag
            ? () => navigate(getDocumentsUrlWithCurrentParams())
            : status === 'duplicate'
              ? handleDuplicate
              : handleContinue
        }
        submitLabel={
          stepFlag.hasActiveStepFlag
            ? 'Return to Work Queue'
            : status === 'duplicate'
              ? 'Return to Work Queue'
              : 'Validate'
        }
        isScrolled={isScrolledBottom}
        previousStepUrl={getDocumentStepUrl(
          document.key,
          isNonFinancial ? 'classification' : 'eob_payment_deposit',
          { queryParams },
        )}
        previousStepLabel={isNonFinancial ? 'Go to Classification' : 'Go to Payment Deposit'}
      />

      {sentinelElementBottom}
    </div>
  )
}
